import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import { Arrow, Chevron } from "@components/icons"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body14Styles,
  body16MainStyles,
  h5Styles,
  smallcapsBold13Styles,
} from "@constants/typography"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import loadable from "@loadable/component"
import { withFinePrint } from "@utils/ContentfulRichText"
import { emptyRichText } from "@utils/richText"
import { Link } from "gatsby"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import React from "react"
import styled, { css, keyframes } from "styled-components"

const Slider = loadable(() => import("react-slick"), { ssr: false })

import LinkButton, {
  Button as LinkButtonContainer,
  TextWrapper as LinkButtonText,
} from "../buttons/LinkButton"
import { PromoBannerProps } from "./common"

const baseButtonStyles = css`
  border: none;
  background-color: transparent;
  outline: none;

  &:hover {
    cursor: pointer;
  }

  &:focus {
    outline: none;
  }

  &:active {
    outline: none;
  }
`

const disabledStyles = css`
  visibility: hidden;
  pointer-events: none;
`

const Container = styled.div`
  display: none;
  height: 36px;

  @media ${breakpoints.mediumLaptop} {
    display: initial;
  }
`

const ExpandedContainer = styled.div<{ expanded: boolean }>`
  ${({ expanded }) => css`
    z-index: 2;
    position: absolute;
    top: 0;
    left: 0;
    transform: translateY(${expanded ? "0" : "-100%"});
    transition: transform ${animation.medium} ${cubicBezier};
    width: 100%;
    background-color: ${colors.promoBannerDesktopGraySeriously};
  `}
`

const ExpandedContentRow = styled.div`
  display: flex;
`

const ExpandedPrevButton = styled.button<{ disabled: boolean }>`
  ${({ disabled }) => css`
    ${baseButtonStyles}
    width: calc(2 / 12 * 100vw);
    margin-right: calc(1 / 12 * 100vw);
    padding-left: calc(1 / 12 * 100vw);

    ${disabled && disabledStyles}
  `}
`

const ExpandedNextButton = styled.button`
  ${({ disabled }) => css`
    ${baseButtonStyles}
    width: calc(2 / 12 * 100vw);
    margin-left: calc(1 / 12 * 100vw);
    padding-right: calc(1 / 12 * 100vw);

    ${disabled && disabledStyles}
  `}
`

const ExpandedPrevArrow = styled.div`
  transform: rotate(180deg);
  width: 24px;
  height: 24px;
`

const ExpandedNextArrow = styled(ExpandedPrevArrow)`
  transform: none;
`

const ExpandedSliderWrapper = styled.div`
  width: calc(6 / 12 * 100vw);
  overflow: hidden;
`

const ExpandedPromoWrapper = styled.div`
  &:focus {
    outline: none;
  }

  &:active {
    outline: none;
  }
`

const ExpandedPromo = styled.div`
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  display: flex;
  width: 100%;
  padding-top: 62px;
`

const ExpandedHeader = styled.div`
  ${h5Styles}
`

const ExpandedBody = styled.div`
  ${body16MainStyles};
  margin: 42px 0;
  color: ${colors.mediumGray};

  p {
    margin: 0 0 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    margin: 0 0 20px;
    padding-left: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const ActionLink = styled(LinkButton)`
  margin-bottom: 80px;

  ${LinkButtonContainer} {
    width: initial;
  }

  ${LinkButtonText} {
    padding-right: 36px;
  }
`

const CollapseButton = styled.button`
  ${baseButtonStyles}
  align-items: center;
  justify-content: center;
  display: flex;
  width: 100%;
  height: 78px;

  border-top: 1px solid ${colors.dividerLineGray};

  &:hover {
    background-color: ${colors.dividerLineGray};
  }
`

const CollapseIconWrapper = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  transform: rotate(-90deg);
  width: 36px;
  height: 36px;
`

const Content = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  height: 36px;
  padding-right: 20px;
  background-color: ${colors.offBlack};
`

const Links = styled.div`
  flex-grow: 1;
  align-items: center;
  text-align: end;
`

const linkStyles = css`
  ${body14Styles}
  margin-left: 25px;
`

const TextButton = styled.button`
  ${body14Styles}
  margin: 0;
  margin-left: 25px;
  padding: 0;
  border: none;
  background: none;
  transition: color ${animation.quick} ${cubicBezier};
  color: ${colors.mediumGray};
  opacity: 1;

  &:hover {
    color: ${colors.offWhite};
    cursor: pointer;
  }
`

const SupportLink = styled.a`
  ${linkStyles}
  transition: color ${animation.quick} ${cubicBezier};
  color: ${colors.mediumGray};
  opacity: 1;

  &:hover {
    color: ${colors.offWhite};
  }
`

const AccountLink = styled(Link as any)`
  ${linkStyles}
  color: ${colors.mediumGray};
  opacity: 1;

  &:hover {
    color: ${colors.offWhite};
  }
`

const RewardsLink = styled(Link as any)`
  ${linkStyles}
  font: ${smallcapsBold13Styles};
  color: ${colors.white};
  background: #f26722;
  padding: 3px 7px 3px 7px;
  border-radius: 2px;
`

const BannerSliderColumn = styled.div`
  position: absolute;
  left: 50%;
  display: flex;
  transform: translateX(-50%);
  max-width: 37.5%;
`

const BannerSliderWrapper = styled.div`
  width: 458px;
  height: 36px;
  overflow: hidden;

  @media ${breakpoints.desktop} {
    width: 601px;
  }

  .slick-track {
    height: 36px;
    overflow: hidden;
  }
`

const BannerPromoWrapper = styled.div``

const BannerPromo = styled.button`
  ${baseButtonStyles}
  align-items: center;
  display: flex;
  width: 100%;
  height: 36px;
  padding: 0 16px;
  border-radius: 2px;
`

const BannerPromoHeader = styled.div`
  ${body14Styles}
  opacity: 1;
  width: 100%;
  padding: 3.5px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${colors.offWhite};

  &:hover {
    border-radius: 2px;
    background-color: ${colors.black30};
  }
`

const bannerHoverStyles = css`
  > svg:hover {
    background-color: ${colors.black30};
    border-radius: 2px;
    color: ${colors.offWhite};
  }
`

const BannerPrevButton = styled.button<{ disabled: boolean }>`
  color: ${colors.mediumDarkGray};
  opacity: 1;

  ${({ disabled }) => css`
    ${baseButtonStyles}
    align-items: center;
    justify-content: center;
    display: flex;
    transform: rotate(180deg);
    width: 36px;
    height: 36px;
    padding: 6px;

    ${disabled && disabledStyles}

    ${!disabled && bannerHoverStyles}
  `}
`

const BannerNextButton = styled(BannerPrevButton)`
  transform: none;
`

const fadeIn = keyframes`
  0% {
    background-color: rgb(0, 0, 0, 0);
  }
  
  100% {
    background-color: rgb(0, 0, 0, 0.5);
  }
`

const BackgroundOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background-color: rgb(0, 0, 0, 0.5);
  animation: ${fadeIn} ease 0.5s;
`

const DesktopPromoBanner: React.FC<PromoBannerProps> = ({
  enterpriseLink,
  isLoggedIn,
  onLogout,
  promos,
  promoScrollDelay,
  rewardsPoints,
  showRewardsPoints,
  supportLink,
}) => {
  const [expanded, setExpanded] = React.useState<boolean>(false)

  const expandBody = () => {
    setExpanded(true)
  }

  const collapseBody = () => {
    setExpanded(false)
  }

  const bannerSliderRef = React.useRef<any>(null)
  const expandedSliderRef = React.useRef<any>(null)

  const onPrevSlide = () => {
    if (
      bannerSliderRef &&
      bannerSliderRef.current &&
      expandedSliderRef &&
      expandedSliderRef.current
    ) {
      bannerSliderRef.current.slickPrev()
      expandedSliderRef.current.slickPrev()
    }
  }

  const onNextSlide = () => {
    if (
      bannerSliderRef &&
      bannerSliderRef.current &&
      expandedSliderRef &&
      expandedSliderRef.current
    ) {
      bannerSliderRef.current.slickNext()
      expandedSliderRef.current.slickNext()
    }
  }

  const expandedContainerRef: any = React.useRef(null)

  useModalBackgroundClose({
    isOpen: expanded,
    modalRef: expandedContainerRef,
    onRequestClose: collapseBody,
  })

  const [activeSlide, setActiveSlide] = React.useState<number>(0)
  const [activeExpandedSlide, setActiveExpandedSlide] =
    React.useState<number>(0)

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      if (!expanded) {
        onNextSlide()
      }
    }, parseInt(promoScrollDelay))

    return () => {
      clearTimeout(timeout)
    }
  }, [activeSlide, expanded, promoScrollDelay])

  return (
    <Container>
      {expanded && <BackgroundOverlay />}
      <ExpandedContainer expanded={expanded} ref={expandedContainerRef}>
        <ExpandedContentRow>
          <ExpandedPrevButton
            aria-label="Previous Promo"
            disabled={promos.length < 2}
            onClick={onPrevSlide}
          >
            <ExpandedPrevArrow>
              <Arrow strokeColor={colors.mediumGray} />
            </ExpandedPrevArrow>
          </ExpandedPrevButton>
          <ExpandedSliderWrapper>
            <Slider
              arrows={false}
              autoplay={false}
              beforeChange={(_, next) => setActiveSlide(next)}
              dots={false}
              draggable={false}
              infinite
              ref={expandedSliderRef}
            >
              {promos.map(
                ({ body, buttonText, externalLink, header, link }, i) => (
                  <ExpandedPromoWrapper
                    hidden={!expanded && activeSlide === i ? true : undefined}
                    key={`expanded-${i}-${header}`}
                  >
                    <ExpandedPromo>
                      <ExpandedHeader>{header}</ExpandedHeader>
                      <ExpandedBody>
                        {renderRichText(
                          (body as any) || emptyRichText,
                          withFinePrint
                        )}
                      </ExpandedBody>
                      {link && (
                        <ActionLink text={buttonText || "Go"} to={link!} />
                      )}
                      {externalLink && (
                        <ActionLink
                          external={true}
                          target="_blank"
                          text={buttonText || "Go"}
                          to={externalLink}
                        />
                      )}
                    </ExpandedPromo>
                  </ExpandedPromoWrapper>
                )
              )}
            </Slider>
          </ExpandedSliderWrapper>
          <ExpandedNextButton
            aria-label="Next Promo"
            disabled={promos.length < 2}
            onClick={onNextSlide}
          >
            <ExpandedNextArrow>
              <Arrow strokeColor={colors.mediumGray} />
            </ExpandedNextArrow>
          </ExpandedNextButton>
        </ExpandedContentRow>
        <CollapseButton aria-label="Collapse Promo" onClick={collapseBody}>
          <CollapseIconWrapper>
            <Chevron strokeColor={colors.mediumDarkGray} />
          </CollapseIconWrapper>
        </CollapseButton>
      </ExpandedContainer>
      <Content>
        <BannerSliderColumn>
          <BannerPrevButton
            aria-label="Previous Promo"
            disabled={promos.length < 2}
            onClick={onPrevSlide}
          >
            <Chevron strokeColor="currentColor" />
          </BannerPrevButton>
          <BannerSliderWrapper>
            <Slider
              arrows={false}
              autoplay={false}
              beforeChange={(_, next) => setActiveExpandedSlide(next)}
              dots={false}
              draggable={false}
              infinite
              ref={bannerSliderRef}
            >
              {promos.map(({ header }, i) => (
                <BannerPromoWrapper
                  hidden={
                    expanded && activeExpandedSlide === i ? true : undefined
                  }
                  key={`banner-${i}-${header}`}
                >
                  <BannerPromo
                    onClick={(e) => {
                      e.stopPropagation()
                      expandBody()
                    }}
                  >
                    <BannerPromoHeader>{header}</BannerPromoHeader>
                  </BannerPromo>
                </BannerPromoWrapper>
              ))}
            </Slider>
          </BannerSliderWrapper>
          <BannerNextButton
            aria-label="Next Promo"
            disabled={promos.length < 2}
            onClick={onNextSlide}
          >
            <Chevron strokeColor="currentColor" />
          </BannerNextButton>
        </BannerSliderColumn>
        <Links>
          <SupportLink href={enterpriseLink} rel="noopener" target="_blank">
            Enterprise
          </SupportLink>
          <SupportLink href={supportLink} rel="noopener" target="_blank">
            Support
          </SupportLink>
          {!showRewardsPoints && (
            <AccountLink state={{ ref: "rewards" }} to="/rewards">
              Rewards
            </AccountLink>
          )}
          <AccountLink to="/account">Account</AccountLink>
          {isLoggedIn && <TextButton onClick={onLogout}>Logout</TextButton>}
          {showRewardsPoints && rewardsPoints && (
            <RewardsLink to="/rewards">{rewardsPoints} Points</RewardsLink>
          )}
        </Links>
      </Content>
    </Container>
  )
}

export default DesktopPromoBanner
